import { HigherApiClient } from 'src/api/client/ApiClient'
import { WarehouseApi } from 'src/business/Warehouse/WarehouseApi'
import { AppConfig } from 'src/context/AppConfig'
import { OrderApi } from 'src/business/ordering/OrderApi'
import {
    AppRouter,
    createAppContextApi,
    createPersistentStore,
    StorageDataCompatibilityGuard
} from '@core'
import { ReadOnlyStore, Store } from '@core/types'
import { pageList } from 'src/routing/pages'
import Axios, { AxiosInstance } from 'axios'
import { setupAuth } from 'src/auth/setupAuth'
import { UserService } from 'src/auth/user/UserService'
import { ClientUser } from 'src/api/defs/ClientUser.type'
import { OrderStore } from 'src/business/ordering/order/OrderStore'
import { MyZoneApi } from 'src/business/my-zone/MyZoneApi'
import { Language } from 'src/api/defs/enums.type'
import { extractHttpError } from 'src/http/HttpError'
import { DevOrderStore } from 'dev/DevOrderStore'
import { ContractApi } from 'src/business/Contract/ContractApi'
import { PreviousLocationDetector } from 'src/routing/PreviousLocationDetector'
import { UserSession } from 'src/auth/SessionHolder'
import { StoreDebugger } from 'src/lib/StoreDebugger'
import { Gtm } from 'src/meta/Gtm'
import { ErrorAppContext, errorAppCtx } from 'src/error/errorAppContext'
import { FranchisingApi } from 'src/business/Franchising/FranchisingApi'
import { NewsletterApi } from '../business/my-zone/NewsletterApi'
import { UserCommonLockApi } from '../business/locks/UserCommonLockApi'
import { AdminCommonUnlockTokenApi } from 'src/business/locks/AdminCommonUnlockTokenApi'
import {
    getGeolocation,
    isAndroidNativeApp,
    isIosNativeApp,
    invalidateCachedCoords,
    saveCoords
} from '../geo/locationService'

export type AppContext = {
    config: AppConfig
    router: AppRouter
    // TODO move to router or lib ?
    prevLocationDetector: PreviousLocationDetector
    api: HigherApiClient
    warehouse: {
        api: WarehouseApi
    }
    lockV2: {
        api: UserCommonLockApi
    }
    unlockToken: {
        api: AdminCommonUnlockTokenApi
    }
    orderApi: OrderApi
    error: ErrorAppContext
    user: {
        service: UserService
        store: Store<ClientUser | null>
        sessionStore: ReadOnlyStore<boolean | null>
    }
    order: {
        store: OrderStore
    }
    contract: {
        api: ContractApi
    }
    franchising: {
        api: FranchisingApi
    }
    myZoneApi: MyZoneApi
    newsletterApi: NewsletterApi
    i18n: {
        language: Store<Language>
    }
    http: AxiosInstance
    session: UserSession
    gtm: Gtm
}

// TODO move to the core lib
const scrollToTopOnPathChange = (r: AppRouter) => {
    let path = r.getLocation().path

    r.locationChanges().subscribe(l => {
        if (l.path !== path) {
            window.scrollTo(0, 0)
        }
        path = l.path
    })
}

const storageDataSchemaVersion = '1.0'

const resolveNativeBundleApiBaseUrl = (
    config: AppConfig,
    win: Window
): string | null => {
    const isNativeApp = isIosNativeApp() || isAndroidNativeApp()
    if (!isNativeApp) return null

    const location = win.location
    const protocol = location?.protocol || ''
    const hostname = (location?.hostname || '').toLowerCase()
    const isLocalhostHost =
        hostname === 'localhost' ||
        hostname === '127.0.0.1' ||
        hostname === '::1'

    const isLocalBundleOrigin =
        protocol === 'capacitor:' ||
        protocol === 'ionic:' ||
        protocol === 'file:' ||
        ((protocol === 'http:' || protocol === 'https:') && isLocalhostHost)

    if (!isLocalBundleOrigin) return null

    const primaryProxy = config.proxy?.[0]
    if (!primaryProxy) return null

    return primaryProxy.replace(/\/+$/, '')
}

export const createAppContext = (
    config: AppConfig,
    win: Window
): AppContext => {
    const router = new AppRouter(pageList, {
        onFetchPageError: err => {
            console.log('Page fetch error ' + err.message)
            if (err.name === 'ChunkLoadError') {
                console.error('Is chunk page loading error. Will reload.')
                win.location.reload()
            }
        }
    })

    const storage = win.localStorage
    new StorageDataCompatibilityGuard(storage).ensure(storageDataSchemaVersion)
    scrollToTopOnPathChange(router)

    const apiBaseUrl = resolveNativeBundleApiBaseUrl(config, win)
    const http = Axios.create(apiBaseUrl ? { baseURL: apiBaseUrl } : undefined)
    if (apiBaseUrl) {
        console.log('[api] Native bundle base URL:', apiBaseUrl)
    }

    const api = new HigherApiClient(http)

    const dbgr = new StoreDebugger()
    const auth = setupAuth(http, api, dbgr)

    const session = new UserSession(storage)
    http.interceptors.request.use(auth.requestInterceptor)
    http.interceptors.request.use(r => {
        const sidHeader = 'Sid'
        r.headers[sidHeader] = session.getPersistentSessionId()
        return r
    })
    http.interceptors.response.use(
        x => x,
        async err => {
            if (err?.response?.status === 400) {
                if (err?.response?.data?.code === 'lock-v2/too-far-away') {
                    invalidateCachedCoords()
                    try {
                        const coords = await getGeolocation(false)
                        saveCoords(coords)
                    } catch (refreshError) {
                        console.warn(
                            'Failed to refresh geolocation after too-far-away response:',
                            refreshError
                        )
                    }
                }
            }
            return Promise.reject(extractHttpError(err))
        }
    )
    http.interceptors.response.use(
        x => x,
        err => {
            auth.onHttpError(err)
            return Promise.reject(err)
        }
    )

    const orderStore = new OrderStore()

    auth.service.$login().subscribe(user => {
        const order = orderStore.value()
        // If logged then change invoicing details in existing order
        if (order && order.invoicingDetails)
            orderStore.prefillOrderFromUser(user)
    })

    const warehouseApi = new WarehouseApi(api)

    const ctx = {
        config,
        router,
        api,
        warehouse: {
            api: warehouseApi
        },
        lockV2: {
            api: new UserCommonLockApi(api)
        },
        unlockToken: {
            api: new AdminCommonUnlockTokenApi(api)
        },
        // TODO move unto 'order' part of ctx
        orderApi: new OrderApi(api),
        user: {
            service: auth.service,
            store: auth.userStore,
            sessionStore: auth.sessionStore
        },
        order: {
            store: config.devMode ? DevOrderStore(orderStore) : orderStore
        },
        contract: {
            api: new ContractApi(api)
        },
        franchising: {
            api: new FranchisingApi(api)
        },
        myZoneApi: new MyZoneApi(api),
        newsletterApi: new NewsletterApi(api),
        i18n: {
            language: createPersistentStore(
                storage,
                'user.lang',
                config.defaultLocale
            )
        },
        http,
        prevLocationDetector: new PreviousLocationDetector(router.history),
        session,
        gtm: new Gtm(win),
        error: errorAppCtx(
            storage,
            session,
            () => auth.userStore.value()?.email || null,
            win,
            api
        )
    }

    dbgr.addStores({
        user: ctx.user.store
    })

    if (config.devMode) dbgr.startShowingChanges()

    auth.loadTokenFromStorage()

    const isLocaleSet = JSON.parse(
        window.localStorage.getItem('user.lang') || 'null'
    )

    // set default user.lang if not set
    if (!isLocaleSet) {
        localStorage.setItem('user.lang', JSON.stringify(config.defaultLocale))
    }

    return ctx
}

export const { AppContextProvider, getAppContext } =
    createAppContextApi<AppContext>()
